import React, { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Home, Calendar, Bus, ShoppingCart, Settings, LogOut, User, RefreshCw, Menu, X, CreditCard, LifeBuoy } from 'lucide-react'
import { useAuthStore } from '../stores/authStore'

interface LayoutProps {
  children: React.ReactNode
}

export default function Layout({ children }: LayoutProps) {
  const location = useLocation()
  const { profile, signOut, isAdmin } = useAuthStore()
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  // Add keyframe animations to the document head if not already present
  React.useEffect(() => {
    if (!document.getElementById('fc-frick-animations')) {
      const style = document.createElement('style')
      style.id = 'fc-frick-animations'
      style.textContent = `
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
        @media (min-width: 768px) {
          .desktop-nav { display: flex !important; }
          .desktop-user { display: flex !important; }
          .mobile-menu-btn { display: none !important; }
        }
      `
      document.head.appendChild(style)
    }
  }, [])

  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/')
  }

  const handleNavigation = (targetPath: string, e: React.MouseEvent) => {
    e.preventDefault()
    
    // Close mobile menu when navigating
    setIsMobileMenuOpen(false)
    
    // Check if we're already on the target page
    const currentPath = location.pathname
    const isAlreadyOnPage = currentPath === targetPath || currentPath.startsWith(targetPath + '/')
    
    if (isAlreadyOnPage) {
      // Already on this page, just return
      return
    }
    
    // Navigate to new page with forced refresh
    window.location.href = targetPath
  }

  const handleSignOut = async () => {
    if (confirm('Möchten Sie sich wirklich abmelden?')) {
      setIsMobileMenuOpen(false) // Close mobile menu
      try {
        await signOut()
      } catch (error) {
        // signOut raeumt intern bereits lokal auf; hier nur verhindern, dass
        // eine Rejection den Wechsel zur Login-Seite verschluckt.
        console.warn('Abmelden meldete einen Fehler:', error)
      }
      // Harte Navigation statt auf das Neu-Rendern von ProtectedRoute zu warten
      // — dieselbe Konvention wie handleNavigation weiter oben.
      window.location.href = '/login'
    }
  }

  const handleRefresh = () => {
    console.log('Refreshing page...')
    setIsMobileMenuOpen(false) // Close mobile menu
    window.location.reload()
  }

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen)
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      background: 'linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%)'
    }}>
      <nav style={{
        background: 'linear-gradient(135deg, #1B7E3A 0%, #22C55E 100%)',
        borderBottom: '3px solid #15803D',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        position: 'sticky',
        top: '0',
        zIndex: '50'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '0 16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          height: '64px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center'
          }}>
            <img 
              src="/small-fc-frick-logo.png" 
              alt="FC Frick Logo" 
              style={{
                height: '32px',
                width: 'auto',
                marginRight: '12px',
                filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2))'
              }}
            />
            <Link 
              to="/" 
              style={{
                fontSize: '20px',
                fontWeight: '800',
                color: 'white',
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.2s ease',
                letterSpacing: '-0.025em'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-1px)'
                e.currentTarget.style.textShadow = '0 2px 4px rgba(0, 0, 0, 0.2)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)'
                e.currentTarget.style.textShadow = 'none'
              }}
            >
              ⚽ FC Frick
            </Link>
          </div>

          {/* Desktop Navigation */}
          <ul style={{
            display: 'none',
            listStyle: 'none',
            gap: '8px',
            alignItems: 'center',
            margin: '0',
            padding: '0'
          }} className="desktop-nav">
            <li>
              <Link 
                to="/" 
                onClick={(e) => handleNavigation('/', e)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 16px',
                  color: isActive('/') && location.pathname === '/' ? '#FFF' : 'rgba(255, 255, 255, 0.9)',
                  textDecoration: 'none',
                  borderRadius: '12px',
                  transition: 'all 0.2s ease',
                  fontSize: '14px',
                  fontWeight: '500',
                  position: 'relative',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  background: isActive('/') && location.pathname === '/' ? 'rgba(255, 255, 255, 0.2)' : 'transparent',
                  backdropFilter: isActive('/') && location.pathname === '/' ? 'blur(10px)' : 'none'
                }}
                onMouseEnter={(e) => {
                  if (!(isActive('/') && location.pathname === '/')) {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)'
                    e.currentTarget.style.color = '#FFF'
                    e.currentTarget.style.transform = 'translateY(-1px)'
                    e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.1)'
                  }
                }}
                onMouseLeave={(e) => {
                  if (!(isActive('/') && location.pathname === '/')) {
                    e.currentTarget.style.background = 'transparent'
                    e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'
                    e.currentTarget.style.transform = 'translateY(0)'
                    e.currentTarget.style.boxShadow = 'none'
                  }
                }}
              >
                <Home size={16} />
                Home
              </Link>
            </li>
            <li>
              <Link 
                to="/testspiele" 
                onClick={(e) => handleNavigation('/testspiele', e)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 16px',
                  color: isActive('/testspiele') ? '#FFF' : 'rgba(255, 255, 255, 0.9)',
                  textDecoration: 'none',
                  borderRadius: '12px',
                  transition: 'all 0.2s ease',
                  fontSize: '14px',
                  fontWeight: '500',
                  position: 'relative',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  background: isActive('/testspiele') ? 'rgba(255, 255, 255, 0.2)' : 'transparent',
                  backdropFilter: isActive('/testspiele') ? 'blur(10px)' : 'none'
                }}
                onMouseEnter={(e) => {
                  if (!isActive('/testspiele')) {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)'
                    e.currentTarget.style.color = '#FFF'
                    e.currentTarget.style.transform = 'translateY(-1px)'
                    e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.1)'
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive('/testspiele')) {
                    e.currentTarget.style.background = 'transparent'
                    e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'
                    e.currentTarget.style.transform = 'translateY(0)'
                    e.currentTarget.style.boxShadow = 'none'
                  }
                }}
              >
                <Calendar size={16} />
                Testspiele
              </Link>
            </li>
            <li>
              <Link 
                to="/equipment" 
                onClick={(e) => handleNavigation('/equipment', e)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 16px',
                  color: (isActive('/equipment') || isActive('/orders')) ? '#FFF' : 'rgba(255, 255, 255, 0.9)',
                  textDecoration: 'none',
                  borderRadius: '12px',
                  transition: 'all 0.2s ease',
                  fontSize: '14px',
                  fontWeight: '500',
                  position: 'relative',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  background: (isActive('/equipment') || isActive('/orders')) ? 'rgba(255, 255, 255, 0.2)' : 'transparent',
                  backdropFilter: (isActive('/equipment') || isActive('/orders')) ? 'blur(10px)' : 'none'
                }}
                onMouseEnter={(e) => {
                  if (!(isActive('/equipment') || isActive('/orders'))) {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)'
                    e.currentTarget.style.color = '#FFF'
                    e.currentTarget.style.transform = 'translateY(-1px)'
                    e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.1)'
                  }
                }}
                onMouseLeave={(e) => {
                  if (!(isActive('/equipment') || isActive('/orders'))) {
                    e.currentTarget.style.background = 'transparent'
                    e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'
                    e.currentTarget.style.transform = 'translateY(0)'
                    e.currentTarget.style.boxShadow = 'none'
                  }
                }}
              >
                <ShoppingCart size={16} />
                Equipment
              </Link>
            </li>
            <li>
              <Link 
                to="/bus" 
                onClick={(e) => handleNavigation('/bus', e)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 16px',
                  color: isActive('/bus') ? '#FFF' : 'rgba(255, 255, 255, 0.9)',
                  textDecoration: 'none',
                  borderRadius: '12px',
                  transition: 'all 0.2s ease',
                  fontSize: '14px',
                  fontWeight: '500',
                  position: 'relative',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  background: isActive('/bus') ? 'rgba(255, 255, 255, 0.2)' : 'transparent',
                  backdropFilter: isActive('/bus') ? 'blur(10px)' : 'none'
                }}
                onMouseEnter={(e) => {
                  if (!isActive('/bus')) {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)'
                    e.currentTarget.style.color = '#FFF'
                    e.currentTarget.style.transform = 'translateY(-1px)'
                    e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.1)'
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive('/bus')) {
                    e.currentTarget.style.background = 'transparent'
                    e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'
                    e.currentTarget.style.transform = 'translateY(0)'
                    e.currentTarget.style.boxShadow = 'none'
                  }
                }}
              >
                <Bus size={16} />
                Bus
              </Link>
            </li>
            <li>
              <Link
                to="/support"
                onClick={(e) => handleNavigation('/support', e)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 16px',
                  color: isActive('/support') ? '#FFF' : 'rgba(255, 255, 255, 0.9)',
                  textDecoration: 'none',
                  borderRadius: '12px',
                  transition: 'all 0.2s ease',
                  fontSize: '14px',
                  fontWeight: '500',
                  position: 'relative',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  background: isActive('/support') ? 'rgba(255, 255, 255, 0.2)' : 'transparent',
                  backdropFilter: isActive('/support') ? 'blur(10px)' : 'none'
                }}
                onMouseEnter={(e) => {
                  if (!isActive('/support')) {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)'
                    e.currentTarget.style.color = '#FFF'
                    e.currentTarget.style.transform = 'translateY(-1px)'
                    e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.1)'
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive('/support')) {
                    e.currentTarget.style.background = 'transparent'
                    e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'
                    e.currentTarget.style.transform = 'translateY(0)'
                    e.currentTarget.style.boxShadow = 'none'
                  }
                }}
              >
                <LifeBuoy size={16} />
                Support
              </Link>
            </li>
            {isAdmin() && (
              <li>
                <Link
                  to="/admin/karten"
                  onClick={(e) => handleNavigation('/admin/karten', e)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 16px',
                    color: location.pathname === '/admin/karten' ? '#FFF' : 'rgba(255, 255, 255, 0.9)',
                    textDecoration: 'none', borderRadius: '12px', fontSize: '14px', fontWeight: '500', whiteSpace: 'nowrap',
                    background: location.pathname === '/admin/karten' ? 'rgba(255, 255, 255, 0.2)' : 'transparent'
                  }}
                >
                  <CreditCard size={16} />
                  Karten
                </Link>
              </li>
            )}
            {isAdmin() && (
              <li>
                <Link
                  to="/admin"
                  onClick={(e) => handleNavigation('/admin', e)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px 16px',
                    color: isActive('/admin') ? '#FFF' : 'rgba(255, 255, 255, 0.9)',
                    textDecoration: 'none',
                    borderRadius: '12px',
                    transition: 'all 0.2s ease',
                    fontSize: '14px',
                    fontWeight: '500',
                    position: 'relative',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    background: isActive('/admin') ? 'rgba(255, 255, 255, 0.2)' : 'transparent',
                    backdropFilter: isActive('/admin') ? 'blur(10px)' : 'none'
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive('/admin')) {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)'
                      e.currentTarget.style.color = '#FFF'
                      e.currentTarget.style.transform = 'translateY(-1px)'
                      e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.1)'
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive('/admin')) {
                      e.currentTarget.style.background = 'transparent'
                      e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'
                      e.currentTarget.style.transform = 'translateY(0)'
                      e.currentTarget.style.boxShadow = 'none'
                    }
                  }}
                >
                  <Settings size={16} />
                  Admin
                </Link>
              </li>
            )}
          </ul>
          
          {/* Desktop User Info */}
          <div style={{
            display: 'none',
            alignItems: 'center',
            gap: '8px'
          }} className="desktop-user">
            <span style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              color: 'white',
              fontSize: '14px',
              fontWeight: '500',
              padding: '8px 12px',
              background: 'rgba(255, 255, 255, 0.1)',
              borderRadius: '10px',
              backdropFilter: 'blur(10px)'
            }}>
              <User size={16} />
              {profile?.name || profile?.email}
              {profile?.role === 'admin' && (
                <span style={{
                  background: 'linear-gradient(135deg, #F59E0B 0%, #F97316 100%)',
                  color: 'white',
                  fontSize: '10px',
                  fontWeight: '700',
                  padding: '2px 6px',
                  borderRadius: '6px',
                  marginLeft: '4px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  Admin
                </span>
              )}
            </span>
            <button 
              onClick={handleRefresh}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 12px',
                color: 'rgba(255, 255, 255, 0.9)',
                background: 'transparent',
                border: 'none',
                borderRadius: '10px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                fontSize: '14px',
                fontWeight: '500'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)'
                e.currentTarget.style.color = '#FFF'
                e.currentTarget.style.transform = 'translateY(-1px)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent'
                e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'
                e.currentTarget.style.transform = 'translateY(0)'
              }}
              title="Seite aktualisieren"
            >
              <RefreshCw size={16} />
            </button>
            <button 
              onClick={handleSignOut}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 12px',
                color: 'rgba(255, 255, 255, 0.9)',
                background: 'transparent',
                border: 'none',
                borderRadius: '10px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                fontSize: '14px',
                fontWeight: '500'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(239, 68, 68, 0.2)'
                e.currentTarget.style.color = '#FFF'
                e.currentTarget.style.transform = 'translateY(-1px)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent'
                e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'
                e.currentTarget.style.transform = 'translateY(0)'
              }}
              title="Abmelden"
            >
              <LogOut size={16} />
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button 
            onClick={toggleMobileMenu}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '40px',
              height: '40px',
              color: 'white',
              background: 'rgba(255, 255, 255, 0.1)',
              border: 'none',
              borderRadius: '10px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              backdropFilter: 'blur(10px)'
            }}
            className="mobile-menu-btn"
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'
              e.currentTarget.style.transform = 'scale(1.05)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)'
              e.currentTarget.style.transform = 'scale(1)'
            }}
            aria-label="Menu"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu Overlay */}
        {isMobileMenuOpen && (
          <div 
            style={{
              position: 'fixed',
              top: '0',
              left: '0',
              right: '0',
              bottom: '0',
              background: 'rgba(0, 0, 0, 0.5)',
              zIndex: '100',
              backdropFilter: 'blur(4px)',
              animation: 'fadeIn 0.2s ease-out'
            }}
            onClick={() => setIsMobileMenuOpen(false)}
          >
            <div 
              style={{
                position: 'absolute',
                top: '0',
                right: '0',
                width: '280px',
                height: '100vh',
                background: 'linear-gradient(135deg, #1B7E3A 0%, #15803D 100%)',
                boxShadow: '-4px 0 20px rgba(0, 0, 0, 0.3)',
                transform: isMobileMenuOpen ? 'translateX(0)' : 'translateX(100%)',
                transition: 'transform 0.3s ease-out',
                overflow: 'hidden'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{
                padding: '20px',
                borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <img 
                    src="/small-fc-frick-logo.png" 
                    alt="FC Frick Logo" 
                    style={{
                      height: '32px',
                      width: 'auto',
                      marginRight: '12px',
                      filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2))'
                    }}
                  />
                  <span style={{
                    fontSize: '20px',
                    fontWeight: 'bold',
                    color: 'white'
                  }}>
                    FC Frick
                  </span>
                </div>
                <button 
                  onClick={() => setIsMobileMenuOpen(false)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '36px',
                    height: '36px',
                    color: 'white',
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)'
                  }}
                >
                  <X size={20} />
                </button>
              </div>

              <div style={{
                padding: '20px',
                display: 'flex',
                flexDirection: 'column',
                height: 'calc(100vh - 84px)',
                overflowY: 'auto'
              }}>
                {/* User Info */}
                <div style={{
                  padding: '16px',
                  background: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: '12px',
                  marginBottom: '24px',
                  backdropFilter: 'blur(10px)'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: '8px'
                  }}>
                    <User size={18} style={{ marginRight: '8px', color: 'white' }} />
                    <span style={{
                      fontWeight: '500',
                      color: 'white',
                      fontSize: '14px'
                    }}>
                      {profile?.name || profile?.email}
                    </span>
                  </div>
                  {profile?.role === 'admin' && (
                    <span style={{
                      background: 'linear-gradient(135deg, #F59E0B 0%, #F97316 100%)',
                      color: 'white',
                      fontSize: '11px',
                      fontWeight: '700',
                      padding: '4px 8px',
                      borderRadius: '6px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Administrator
                    </span>
                  )}
                </div>

                {/* Navigation Links */}
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                  marginBottom: '24px',
                  flex: '1'
                }}>
                  <Link 
                    to="/" 
                    onClick={(e) => handleNavigation('/', e)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '12px 16px',
                      color: 'white',
                      textDecoration: 'none',
                      borderRadius: '10px',
                      transition: 'all 0.2s ease',
                      fontSize: '16px',
                      fontWeight: '500',
                      background: (isActive('/') && location.pathname === '/') ? 'rgba(255, 255, 255, 0.2)' : 'transparent'
                    }}
                    onMouseEnter={(e) => {
                      if (!(isActive('/') && location.pathname === '/')) {
                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)'
                        e.currentTarget.style.transform = 'translateX(4px)'
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!(isActive('/') && location.pathname === '/')) {
                        e.currentTarget.style.background = 'transparent'
                        e.currentTarget.style.transform = 'translateX(0)'
                      }
                    }}
                  >
                    <Home size={20} />
                    <span>Home</span>
                  </Link>
                  
                  <Link 
                    to="/testspiele" 
                    onClick={(e) => handleNavigation('/testspiele', e)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '12px 16px',
                      color: 'white',
                      textDecoration: 'none',
                      borderRadius: '10px',
                      transition: 'all 0.2s ease',
                      fontSize: '16px',
                      fontWeight: '500',
                      background: isActive('/testspiele') ? 'rgba(255, 255, 255, 0.2)' : 'transparent'
                    }}
                    onMouseEnter={(e) => {
                      if (!isActive('/testspiele')) {
                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)'
                        e.currentTarget.style.transform = 'translateX(4px)'
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isActive('/testspiele')) {
                        e.currentTarget.style.background = 'transparent'
                        e.currentTarget.style.transform = 'translateX(0)'
                      }
                    }}
                  >
                    <Calendar size={20} />
                    <span>Testspiele</span>
                  </Link>
                  
                  <Link 
                    to="/equipment" 
                    onClick={(e) => handleNavigation('/equipment', e)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '12px 16px',
                      color: 'white',
                      textDecoration: 'none',
                      borderRadius: '10px',
                      transition: 'all 0.2s ease',
                      fontSize: '16px',
                      fontWeight: '500',
                      background: (isActive('/equipment') || isActive('/orders')) ? 'rgba(255, 255, 255, 0.2)' : 'transparent'
                    }}
                    onMouseEnter={(e) => {
                      if (!(isActive('/equipment') || isActive('/orders'))) {
                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)'
                        e.currentTarget.style.transform = 'translateX(4px)'
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!(isActive('/equipment') || isActive('/orders'))) {
                        e.currentTarget.style.background = 'transparent'
                        e.currentTarget.style.transform = 'translateX(0)'
                      }
                    }}
                  >
                    <ShoppingCart size={20} />
                    <span>Equipment</span>
                  </Link>
                  
                  <Link 
                    to="/bus" 
                    onClick={(e) => handleNavigation('/bus', e)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '12px 16px',
                      color: 'white',
                      textDecoration: 'none',
                      borderRadius: '10px',
                      transition: 'all 0.2s ease',
                      fontSize: '16px',
                      fontWeight: '500',
                      background: isActive('/bus') ? 'rgba(255, 255, 255, 0.2)' : 'transparent'
                    }}
                    onMouseEnter={(e) => {
                      if (!isActive('/bus')) {
                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)'
                        e.currentTarget.style.transform = 'translateX(4px)'
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isActive('/bus')) {
                        e.currentTarget.style.background = 'transparent'
                        e.currentTarget.style.transform = 'translateX(0)'
                      }
                    }}
                  >
                    <Bus size={20} />
                    <span>Bus</span>
                  </Link>

                  <Link
                    to="/support"
                    onClick={(e) => handleNavigation('/support', e)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '12px 16px',
                      color: 'white',
                      textDecoration: 'none',
                      borderRadius: '10px',
                      transition: 'all 0.2s ease',
                      fontSize: '16px',
                      fontWeight: '500',
                      background: isActive('/support') ? 'rgba(255, 255, 255, 0.2)' : 'transparent'
                    }}
                    onMouseEnter={(e) => {
                      if (!isActive('/support')) {
                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)'
                        e.currentTarget.style.transform = 'translateX(4px)'
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isActive('/support')) {
                        e.currentTarget.style.background = 'transparent'
                        e.currentTarget.style.transform = 'translateX(0)'
                      }
                    }}
                  >
                    <LifeBuoy size={20} />
                    <span>Support</span>
                  </Link>

                  {isAdmin() && (
                    <Link
                      to="/admin/karten"
                      onClick={(e) => handleNavigation('/admin/karten', e)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px',
                        color: 'white', textDecoration: 'none', borderRadius: '10px', fontSize: '16px', fontWeight: '500',
                        background: location.pathname === '/admin/karten' ? 'rgba(255, 255, 255, 0.2)' : 'transparent'
                      }}
                    >
                      <CreditCard size={20} />
                      <span>Karten</span>
                    </Link>
                  )}
                  {isAdmin() && (
                    <Link
                      to="/admin"
                      onClick={(e) => handleNavigation('/admin', e)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '12px 16px',
                        color: 'white',
                        textDecoration: 'none',
                        borderRadius: '10px',
                        transition: 'all 0.2s ease',
                        fontSize: '16px',
                        fontWeight: '500',
                        background: isActive('/admin') ? 'rgba(255, 255, 255, 0.2)' : 'transparent'
                      }}
                      onMouseEnter={(e) => {
                        if (!isActive('/admin')) {
                          e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)'
                          e.currentTarget.style.transform = 'translateX(4px)'
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isActive('/admin')) {
                          e.currentTarget.style.background = 'transparent'
                          e.currentTarget.style.transform = 'translateX(0)'
                        }
                      }}
                    >
                      <Settings size={20} />
                      <span>Admin</span>
                    </Link>
                  )}
                </div>

                {/* Action Buttons */}
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                  borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                  paddingTop: '16px'
                }}>
                  <button 
                    onClick={handleRefresh}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '12px 16px',
                      color: 'white',
                      background: 'rgba(255, 255, 255, 0.1)',
                      border: 'none',
                      borderRadius: '10px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      fontSize: '16px',
                      fontWeight: '500',
                      width: '100%'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'
                      e.currentTarget.style.transform = 'translateX(4px)'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)'
                      e.currentTarget.style.transform = 'translateX(0)'
                    }}
                  >
                    <RefreshCw size={20} />
                    <span>Aktualisieren</span>
                  </button>
                  
                  <button 
                    onClick={handleSignOut}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '12px 16px',
                      color: 'white',
                      background: 'rgba(239, 68, 68, 0.2)',
                      border: 'none',
                      borderRadius: '10px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      fontSize: '16px',
                      fontWeight: '500',
                      width: '100%'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(239, 68, 68, 0.3)'
                      e.currentTarget.style.transform = 'translateX(4px)'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(239, 68, 68, 0.2)'
                      e.currentTarget.style.transform = 'translateX(0)'
                    }}
                  >
                    <LogOut size={20} />
                    <span>Abmelden</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </nav>
      <main style={{
        flex: '1',
        display: 'flex',
        flexDirection: 'column',
        minHeight: '0'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '0 16px',
          width: '100%',
          flex: '1'
        }}>
          {children}
        </div>
      </main>
    </div>
  )
}
